/**
 * @fileoverview URL utility functions for grade and echelon routing
 * 
 * This module provides functions to convert grade and echelon data to URL-friendly
 * segments and vice versa. It handles special cases like temporary echelons
 * (échelons provisoires) that use negative numbers or special labels.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

import { EchelonData, GradeData } from '../types';

/**
 * Converts a grade ID to a URL segment
 * 
 * @param {number} gradeId - The numeric grade ID (e.g., 1, 2, 3)
 * @returns {string} URL segment for the grade (e.g., "grade-1", "grade-2")
 * 
 * @example
 * const urlSegment = getGradeUrlSegment(1);
 * console.log(urlSegment); // "grade-1"
 * 
 * @example
 * const urlSegment = getGradeUrlSegment(3);
 * console.log(urlSegment); // "grade-3"
 */
export function getGradeUrlSegment(gradeId: number): string {
  return `grade-${gradeId}`;
}

/**
 * Converts an echelon data object to a URL segment
 * 
 * This function handles both regular echelons and temporary echelons:
 * - Regular echelons (1, 2, 3, etc.) become "echelon-1", "echelon-2", etc.
 * - Temporary echelons with labels become URL-friendly versions of their labels
 * - Temporary echelons (-1, 0) use their labels if available, otherwise become "echelon-provisoire-1", etc.
 * 
 * @param {EchelonData} echelon - The echelon data object
 * @returns {string} URL segment for the echelon
 * 
 * @example
 * // Regular echelon
 * const echelon = { echelon: 5, indiceMajoré: 491 };
 * const urlSegment = getEchelonUrlSegment(echelon);
 * console.log(urlSegment); // "echelon-5"
 * 
 * @example
 * // Temporary echelon with label
 * const echelon = { echelon: -1, indiceMajoré: 380, echelonLabel: "(1)" };
 * const urlSegment = getEchelonUrlSegment(echelon);
 * console.log(urlSegment); // "echelon-provisoire-1"
 * 
 * @example
 * // Temporary echelon without label
 * const echelon = { echelon: 0, indiceMajoré: 360 };
 * const urlSegment = getEchelonUrlSegment(echelon);
 * console.log(urlSegment); // "echelon-provisoire-2"
 */
export function getEchelonUrlSegment(echelon: EchelonData): string {
  // Handle positive echelons (standard case)
  if (echelon.echelon > 0) {
    return `echelon-${echelon.echelon}`;
  }
  
  // Handle temporary echelons (-1 and 0)
  if (echelon.echelonLabel) {
    // Extract number from label like "(1)" or "(2)"
    const labelMatch = echelon.echelonLabel.match(/\((\d+)\)/);
    if (labelMatch) {
      return `echelon-provisoire-${labelMatch[1]}`;
    }
    
    // If label doesn't match expected pattern, create URL-friendly version
    const urlFriendlyLabel = echelon.echelonLabel
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    return `echelon-${urlFriendlyLabel}`;
  }
  
  // Fallback for temporary echelons without labels
  // Map: -1 → provisoire-1, 0 → provisoire-2
  const provisoireNumber = echelon.echelon === -1 ? 1 : 2;
  return `echelon-provisoire-${provisoireNumber}`;
}

/**
 * Parses a grade URL segment to extract the grade ID
 * 
 * @param {string} segment - URL segment (e.g., "grade-1", "grade-2")
 * @returns {number | null} The grade ID or null if invalid
 * 
 * @example
 * const gradeId = parseGradeUrlSegment("grade-1");
 * console.log(gradeId); // 1
 * 
 * @example
 * const gradeId = parseGradeUrlSegment("invalid-segment");
 * console.log(gradeId); // null
 */
export function parseGradeUrlSegment(segment: string): number | null {
  const match = segment.match(/^grade-(\d+)$/);
  if (match) {
    const gradeId = parseInt(match[1], 10);
    return isNaN(gradeId) ? null : gradeId;
  }
  return null;
}

/**
 * Parses an echelon URL segment to extract echelon information
 * 
 * This function handles both regular and temporary echelon URL segments:
 * - "echelon-5" → { echelonNum: 5, echelonLabel: null }
 * - "echelon-provisoire-1" → { echelonNum: -1, echelonLabel: "(1)" }
 * - "echelon-provisoire-2" → { echelonNum: 0, echelonLabel: "(2)" }
 * 
 * @param {string} segment - URL segment (e.g., "echelon-5", "echelon-provisoire-1")
 * @returns {object} Object with echelon number and label information
 * 
 * @example
 * const result = parseEchelonUrlSegment("echelon-5");
 * console.log(result); // { echelonNum: 5, echelonLabel: null }
 * 
 * @example
 * const result = parseEchelonUrlSegment("echelon-provisoire-1");
 * console.log(result); // { echelonNum: -1, echelonLabel: "(1)" }
 */
export function parseEchelonUrlSegment(segment: string): { 
  echelonNum: number | null; 
  echelonLabel: string | null;
} {
  // Handle regular echelons: "echelon-5"
  const regularMatch = segment.match(/^echelon-(\d+)$/);
  if (regularMatch) {
    const echelonNum = parseInt(regularMatch[1], 10);
    return {
      echelonNum: isNaN(echelonNum) ? null : echelonNum,
      echelonLabel: null
    };
  }
  
  // Handle temporary echelons: "echelon-provisoire-1", "echelon-provisoire-2"
  const provisoireMatch = segment.match(/^echelon-provisoire-(\d+)$/);
  if (provisoireMatch) {
    const provisoireNum = parseInt(provisoireMatch[1], 10);
    if (!isNaN(provisoireNum)) {
      // Convert provisoire number to temporary echelon number
      // provisoire-1 → echelon -1, provisoire-2 → echelon 0
      const echelonNum = provisoireNum === 1 ? -1 : 0;
      const echelonLabel = `(${provisoireNum})`;
      
      return {
        echelonNum,
        echelonLabel
      };
    }
  }
  
  // Handle other custom echelon formats
  const customMatch = segment.match(/^echelon-(.+)$/);
  if (customMatch) {
    const customPart = customMatch[1];
    
    // Try to extract number from custom part
    const numberMatch = customPart.match(/(\d+)/);
    if (numberMatch) {
      const num = parseInt(numberMatch[1], 10);
      return {
        echelonNum: isNaN(num) ? null : num,
        echelonLabel: customPart
      };
    }
  }
  
  return {
    echelonNum: null,
    echelonLabel: null
  };
}

/**
 * Finds an echelon in a grade's echelons array based on URL parsing result
 * 
 * This function matches the parsed URL segment against the actual echelon data,
 * handling both numeric echelons and labeled temporary echelons.
 * 
 * @param {GradeData} grade - The grade containing echelons to search
 * @param {object} parsedEchelon - Result from parseEchelonUrlSegment
 * @returns {EchelonData | null} The matching echelon or null if not found
 * 
 * @example
 * const grade = { id: 1, nom: "Grade 1", echelons: [...] };
 * const parsed = parseEchelonUrlSegment("echelon-5");
 * const echelon = findEchelonInGrade(grade, parsed);
 * console.log(echelon); // { echelon: 5, indiceMajoré: 491, ... }
 * 
 * @example
 * const grade = { id: 3, nom: "Grade 3", echelons: [...] };
 * const parsed = parseEchelonUrlSegment("echelon-provisoire-1");
 * const echelon = findEchelonInGrade(grade, parsed);
 * console.log(echelon); // { echelon: -1, indiceMajoré: 380, echelonLabel: "(1)", ... }
 */
export function findEchelonInGrade(
  grade: GradeData, 
  parsedEchelon: { echelonNum: number | null; echelonLabel: string | null }
): EchelonData | null {
  if (!grade.echelons || grade.echelons.length === 0) {
    return null;
  }
  
  // If we have a numeric echelon, try to find it directly
  if (parsedEchelon.echelonNum !== null) {
    const foundEchelon = grade.echelons.find(e => e.echelon === parsedEchelon.echelonNum);
    if (foundEchelon) {
      return foundEchelon;
    }
  }
  
  // If we have a label, try to find by label
  if (parsedEchelon.echelonLabel !== null) {
    const foundEchelon = grade.echelons.find(e => e.echelonLabel === parsedEchelon.echelonLabel);
    if (foundEchelon) {
      return foundEchelon;
    }
  }
  
  return null;
}

/**
 * Validates if a grade and echelon combination is valid for a profession
 * 
 * This function checks if the specified grade exists in the profession's grade data
 * and if the echelon exists within that grade.
 * 
 * @param {GradeData[]} grades - Array of grades for the profession
 * @param {number} gradeId - The grade ID to validate
 * @param {number} echelonNum - The echelon number to validate
 * @returns {boolean} True if the combination is valid
 * 
 * @example
 * const isValid = validateGradeEchelonCombination(grades, 1, 5);
 * console.log(isValid); // true if Grade 1 has Echelon 5
 */
export function validateGradeEchelonCombination(
  grades: GradeData[], 
  gradeId: number, 
  echelonNum: number
): boolean {
  const grade = grades.find(g => g.id === gradeId);
  if (!grade) {
    return false;
  }
  
  const echelon = grade.echelons.find(e => e.echelon === echelonNum);
  return echelon !== undefined;
}

/**
 * Gets the default grade and echelon for a profession
 * 
 * This function returns the first grade and first echelon as defaults
 * when no specific grade/echelon is specified in the URL.
 * 
 * @param {GradeData[]} grades - Array of grades for the profession
 * @returns {object} Object with default grade and echelon, or null values if no data
 * 
 * @example
 * const defaults = getDefaultGradeEchelon(grades);
 * console.log(defaults); // { gradeId: 1, echelon: { echelon: 1, ... } }
 */
export function getDefaultGradeEchelon(grades: GradeData[]): {
  gradeId: number | null;
  echelon: EchelonData | null;
} {
  if (!grades || grades.length === 0) {
    return { gradeId: null, echelon: null };
  }
  
  const firstGrade = grades[0];
  if (!firstGrade.echelons || firstGrade.echelons.length === 0) {
    return { gradeId: firstGrade.id, echelon: null };
  }
  
  // Sort echelons to get the first one (could be negative for temporary echelons)
  const sortedEchelons = [...firstGrade.echelons].sort((a, b) => a.echelon - b.echelon);
  const firstEchelon = sortedEchelons[0];
  
  return {
    gradeId: firstGrade.id,
    echelon: firstEchelon
  };
}

/**
 * Extracts grade and echelon information from URL parameters
 * 
 * This function is designed to work with React Router's useParams hook
 * to extract grade and echelon information from URL segments.
 * 
 * @param {Record<string, string | undefined>} params - URL parameters from useParams
 * @returns {object} Parsed grade and echelon information
 * 
 * @example
 * // URL: /infirmiere-anesthesiste-iade/guyane/grade-2/echelon-8
 * const params = { 
 *   professionId: 'infirmiere-anesthesiste-iade', 
 *   territoryId: 'guyane',
 *   gradeId: 'grade-2',
 *   echelonId: 'echelon-8'
 * };
 * const result = extractGradeEchelonFromParams(params);
 * console.log(result); // { gradeId: 2, parsedEchelon: { echelonNum: 8, echelonLabel: null } }
 */
export function extractGradeEchelonFromParams(params: Record<string, string | undefined>): {
  gradeId: number | null;
  parsedEchelon: { echelonNum: number | null; echelonLabel: string | null };
} {
  const gradeId = params.gradeId ? parseGradeUrlSegment(params.gradeId) : null;
  const parsedEchelon = params.echelonId 
    ? parseEchelonUrlSegment(params.echelonId)
    : { echelonNum: null, echelonLabel: null };
  
  return { gradeId, parsedEchelon };
}

/**
 * Checks if URL parameters contain grade and echelon information
 * 
 * @param {Record<string, string | undefined>} params - URL parameters from useParams
 * @returns {boolean} True if both grade and echelon are present in URL
 * 
 * @example
 * const params = { gradeId: 'grade-1', echelonId: 'echelon-5' };
 * const hasGradeEchelon = hasGradeEchelonInUrl(params);
 * console.log(hasGradeEchelon); // true
 */
export function hasGradeEchelonInUrl(params: Record<string, string | undefined>): boolean {
  return !!(params.gradeId && params.echelonId);
}

/**
 * Normalizes an echelon label for URL usage
 * 
 * This function converts echelon labels to URL-friendly formats by:
 * - Converting to lowercase
 * - Replacing non-alphanumeric characters with hyphens
 * - Removing consecutive hyphens
 * - Trimming leading/trailing hyphens
 * 
 * @param {string} label - The echelon label to normalize
 * @returns {string} URL-friendly version of the label
 * 
 * @example
 * const normalized = normalizeEchelonLabel("(1)");
 * console.log(normalized); // "1"
 * 
 * @example
 * const normalized = normalizeEchelonLabel("Échelon provisoire");
 * console.log(normalized); // "echelon-provisoire"
 */
export function normalizeEchelonLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Validates URL segments for grade and echelon
 * 
 * This function checks if URL segments follow the expected patterns
 * for grade and echelon identifiers.
 * 
 * @param {string} gradeSegment - Grade URL segment to validate
 * @param {string} echelonSegment - Echelon URL segment to validate
 * @returns {boolean} True if both segments are valid
 * 
 * @example
 * const isValid = validateUrlSegments("grade-1", "echelon-5");
 * console.log(isValid); // true
 * 
 * @example
 * const isValid = validateUrlSegments("invalid", "echelon-5");
 * console.log(isValid); // false
 */
export function validateUrlSegments(gradeSegment: string, echelonSegment: string): boolean {
  const gradeValid = /^grade-\d+$/.test(gradeSegment);
  const echelonValid = /^echelon-(\d+|provisoire-\d+|[a-z0-9-]+)$/.test(echelonSegment);
  
  return gradeValid && echelonValid;
}